import React, { useState, useEffect } from "react";
import socialSecurityIcon from "../../../assets/social-security-icons/social-security.svg";
import retirementAgeIcon from "../../../assets/social-security-icons/retirement-age.svg";
import retirementDateIcon from "../../../assets/social-security-icons/retirement-date.svg";
import retirementTimeIcon from "../../../assets/social-security-icons/time-remaing-retirement.svg";
import { ToolTipCreator } from "../../../helpers/ToolTipCreator";
import { SocialSecurityFormFields } from "./SocialSecurityFormFields";
import { calculateSocialSecurityResults } from "../../../helpers/socialSecurityCalculations";
import { formatCurrencyAmount } from "../../../helpers/formatAmount";
import { SocialSecurityFormSkeleton } from "../../InsuranceNew/InsuraceDashboardComponents/SocialSecurityFormSkeleton";
import { MODULE_API } from "../../../apis";
import { NumberCardSkeleton } from "../../Skeletons/DebtSkeleton";
import "../styles/socialSecurity.css";
import NewLoader from "../../../components/styled-components/loader/NewLoader";

export const SocialSecurityCalculator = ({ setSocialFieldsData }) => {
  const [results, setResults] = useState(null);
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [spouseSocialFieldsData, setSpouseSocialFieldsData] = useState(null);
  const [loadingAttributes, setLoadingAttributes] = useState(false);
  const [attributes, setAttributes] = useState(null);
  const module = "Social Security Calculator";
  const moduleDataToFetch = "Spouse Social Security vs Retirement Age";

  const handleChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  const formFields = [
    // User
    { label: "Your Birthdate", type: "date", name: "userDob" },
    {
      label: "Your Monthly Benefit at FRA ($)",
      type: "number",
      name: "userPIA",
    },
    { label: "Your Estimated Year of Death", type: "number", name: "userYOD" },
    { label: "Your Desired Claim Age", type: "number", name: "userClaimAge" },

    // Spouse
    { label: "Spouse Birthdate", type: "date", name: "spouseDob" },
    {
      label: "Spouse Monthly Benefit at FRA ($)",
      type: "number",
      name: "spousePIA",
    },
    {
      label: "Spouse Estimated Year of Death",
      type: "number",
      name: "spouseYOD",
    },
    {
      label: "Spouse Desired Claim Age",
      type: "number",
      name: "spouseClaimAge",
    },

    // Tax & Finance
    {
      label: "Tax Filing Status",
      type: "select",
      name: "taxStatus",
      options: [
        { label: "Single", value: "Single" },
        { label: "Married Filing Jointly", value: "Married Filing Jointly" },
        {
          label: "Married Filing Separately (Lived Apart)",
          value: "Married Filing Separately (lived apart)",
        },
        {
          label: "Married Filing Separately (Lived Together)",
          value: "Married Filing Separately (lived together)",
        },
        { label: "Head of Household", value: "Head of Household" },
        { label: "Qualifying Widow(er)", value: "Qualifying Widow(er)" },
      ],
    },
    {
      label: "Other Annual Retirement Income ($)",
      type: "number",
      name: "otherIncome",
    },
    { label: "Discount Rate (TVM, %)", type: "number", name: "tvmRate" },

    // Special cases
    {
      label: "Eligible for WEP/GPO?",
      type: "radio",
      name: "wepEligible",
      options: [
        { label: "Yes", value: true },
        { label: "No", value: false },
      ],
    },
    { label: "WEP/GPO Pension Amount ($)", type: "number", name: "wepAmount" },

    // Dependents
    {
      label: "Do you have eligible dependent children?",
      type: "radio",
      name: "hasDependents",
      options: [
        { label: "Yes", value: true },
        { label: "No", value: false },
      ],
    },
    {
      label: "Number of Dependent Children",
      type: "number",
      name: "numDependents",
    },
    // Child DOBs will be generated dynamically in SocialSecurityFormFields

    // Prior Marriage
    {
      label: "Were you in a prior marriage lasting 10+ years?",
      type: "radio",
      name: "priorMarriage",
      options: [
        { label: "Yes", value: true },
        { label: "No", value: false },
      ],
    },
    { label: "Ex-Spouse Date of Birth", type: "date", name: "exSpouseDob" },
    {
      label: "Ex-Spouse Estimated Monthly Benefit ($)",
      type: "number",
      name: "exSpouseBenefit",
    },

    // Military
    {
      label: "Do you have military service credits (1957-2001)?",
      type: "radio",
      name: "militaryCredits",
      options: [
        { label: "Yes", value: true },
        { label: "No", value: false },
      ],
    },
  ];

  const fetchSpouseData = async () => {
    try {
      // setLoading(true);
      const res = await MODULE_API.fetchItems({
        moduleName: moduleDataToFetch,
        pageNum: 1,
        limit: 10,
        body: {},
      });
      // console.log("resssssss", res);
      if (res && res.records && res.records.length > 0) {
        setSpouseSocialFieldsData(res.records[0]);
      }
    } catch (error) {
      console.error("Error fetching attributes", error);
    } finally {
      // setLoading(false);
    }
  };

  // Fetch Social Security fields
  const getSocialFields = async (Name) => {
    setLoadingAttributes(true);
    try {
      const res = await MODULE_API.assetFiledDetails(Name);
      if (res && res.attributes) {
        const allAttributes = [...res.attributes];
        const sortedAttributes =
          allAttributes &&
          allAttributes.sort(
            (a, b) => a.groupPriorityValue - b.groupPriorityValue
          );
        setAttributes(sortedAttributes);
      }
    } finally {
      setLoadingAttributes(false);
    }
  };

  const handleCalculate = () => {
    if (!formData || !formData["Your Birthdate"]) return;

    if (formData && formData.id) {
      updateRecord();
    } else {
      createAttributes();
    }
    const resultsAll = calculateSocialSecurityResults(formData);

    setResults(resultsAll);
  };

  const createAttributes = async () => {
    try {
      setLoading(true);
      const result = await MODULE_API.addAsset(module, {
        clientModules: [formData],
      });
      if (formData && formData["Spouse Birthdate"] && spouseSocialFieldsData) {
        await MODULE_API.updateClientModule(spouseSocialFieldsData.id, {
          "Spouse Date of Birth": formData["Spouse Birthdate"],
        });
      } else {
        await MODULE_API.addAsset(moduleDataToFetch, {
          clientModules: [
            {
              "Spouse Date of Birth": formData["Spouse Birthdate"],
            },
          ],
        });
      }
      await fetchData();
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };
  const updateRecord = async () => {
    try {
      setLoading(true);
      await MODULE_API.updateClientModule(formData.id, {
        ...formData,
      });
      if (formData && formData["Spouse Birthdate"] && spouseSocialFieldsData) {
        await MODULE_API.updateClientModule(spouseSocialFieldsData.id, {
          "Spouse Date of Birth": formData["Spouse Birthdate"],
        });
      } else {
        await MODULE_API.addAsset(moduleDataToFetch, {
          clientModules: [
            {
              "Spouse Date of Birth": formData["Spouse Birthdate"],
            },
          ],
        });
      }
      await fetchData(module);
      setLoading(false);
    } catch (error) {
      console.error("Error updating record", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await MODULE_API.fetchItems({
        moduleName: module,
        pageNum: 1,
        limit: 10,
        body: {},
      });
      // console.log("resssssss", res);
      if (res && res.records && res.records.length > 0) {
        setFormData(res.records[0]);
        setSocialFieldsData(res.records[0]);
        const resultsAll = calculateSocialSecurityResults(res.records[0]);
        setResults(resultsAll);
      }
    } catch (error) {
      console.error("Error fetching attributes", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getSocialFields(module);
    fetchData(module);
    fetchSpouseData(moduleDataToFetch);
  }, []);

  const resultArray = [
    {
      id: 1,
      name: "Full Retirement Age",
      icon: retirementAgeIcon,
      value:
        results && results.fraResults && results.fraResults.fraAge
          ? results.fraResults.fraAge
          : "N/A",
    },
    {
      id: 2,
      name: "Full Retirement Age Date",
      icon: retirementDateIcon,
      value:
        results && results.fraResults && results.fraResults.fraDate
          ? results.fraResults.fraDate
          : "N/A",
    },
    {
      id: 3,
      name: "Time until Full Retirement Age",
      icon: retirementTimeIcon,
      value:
        results && results.fraResults && results.fraResults.fraCountdown
          ? results.fraResults.fraCountdown
          : "N/A",
    },
  ];
  return (
    <div className="social-security-calculator-main">
      <div className="social-security-main-heading-container">
        <div className="social-security-icon-container">
          <img
            width={32}
            height={32}
            src={socialSecurityIcon}
            alt={"Social Security Calculator"}
          ></img>
        </div>
        <div className="social-security-main-heading">
          Social Security Calculator
          <ToolTipCreator name={"social_security_tooltip"} />
        </div>
      </div>
      <div className="social-security-calculator-content-main">
        <div className="social-security-calculator-content-form-container">
          {loadingAttributes ? (
            <SocialSecurityFormSkeleton rows={4} />
          ) : (
            <div style={{ position: "relative" }}>
              <SocialSecurityFormFields
                formFields={attributes && attributes}
                formData={formData && formData}
                onChange={handleChange}
              />
              <NewLoader isLoading={loading} />
            </div>
          )}
          <button
            className="social-security-calculator-submit-button"
            onClick={(e) => {
              e.preventDefault();
              handleCalculate();
            }}
            disabled={loading || loadingAttributes}
          >
            Calculate
          </button>
        </div>
        <div className="social-security-calculator-results">
          <h3>Social Security Results</h3>
          <div className="social-security-result-tag-container">
            {resultArray &&
              resultArray.map((item, index) => (
                <div
                  key={index}
                  className="social-security-main-heading-container"
                >
                  <div className="social-security-result-icon-container">
                    <img
                      width={24}
                      height={24}
                      src={item.icon}
                      alt={item.name}
                    ></img>
                  </div>
                  <div className="social-security-results-main">
                    <div className="social-result-heading"> {item.name} </div>
                    <div className="social-result-value">
                      {loading ? <NumberCardSkeleton /> : item.value}{" "}
                    </div>
                  </div>
                </div>
              ))}
            {results && (
              <div className="social-security-calculator-results-bottom">
                <h4>Your Desired Ages</h4>

                <div className="social-security-calultor-results">
                  <div className="social-result-heading">Your Benefit:</div>
                  <div className="social-result-value">
                    {results.userBenefit
                      ? formatCurrencyAmount(results.userBenefit)
                      : "$0.00"}
                  </div>
                </div>
                <div className="social-security-calultor-results">
                  <div className="social-result-heading">Spouse Benefit:</div>
                  <div className="social-result-value">
                    {results.spouseBenefit
                      ? formatCurrencyAmount(results.spouseBenefit)
                      : "$0.00"}
                  </div>
                </div>
                <div className="social-security-calultor-results">
                  <div className="social-result-heading">Combined:</div>
                  <div className="social-result-value">
                    {results.combined
                      ? formatCurrencyAmount(results.combined)
                      : "$0.00"}
                  </div>
                </div>
                <div className="social-security-calultor-results">
                  <div className="social-result-heading">Taxable:</div>
                  <div className="social-result-value">
                    {results.taxable
                      ? formatCurrencyAmount(results.taxable)
                      : "$0.00"}
                  </div>
                </div>
                <div className="social-security-calultor-results">
                  <div className="social-result-heading">Lifetime PV:</div>
                  <div className="social-result-value">
                    {results.lifetime
                      ? formatCurrencyAmount(results.lifetime)
                      : "$0.00"}
                  </div>
                </div>
                <div className="social-security-calultor-results">
                  <div className="social-result-heading">Survivor:</div>
                  <div className="social-result-value">
                    {results.survivor
                      ? formatCurrencyAmount(results.survivor)
                      : "$0.00"}
                  </div>
                </div>
                <div className="social-security-calultor-results">
                  <div className="social-result-heading">Breakeven Age:</div>
                  <div className="social-result-value">
                    {results.breakeven || "N/A"}
                  </div>
                </div>

                <h4>Both at Full Retirement Age</h4>

                <div className="social-security-calultor-results">
                  <div className="social-result-heading">Your FRA Benefit:</div>
                  <div className="social-result-value">
                    {results.userFRA_benefit
                      ? formatCurrencyAmount(results.userFRA_benefit)
                      : "$0.00"}
                  </div>
                </div>
                <div className="social-security-calultor-results">
                  <div className="social-result-heading">
                    Spouse FRA Benefit:
                  </div>
                  <div className="social-result-value">
                    {results.spouseFRA_total
                      ? formatCurrencyAmount(results.spouseFRA_total)
                      : "$0.00"}
                  </div>
                </div>
                <div className="social-security-calultor-results">
                  <div className="social-result-heading">Combined FRA:</div>
                  <div className="social-result-value">
                    {results.fraCombined
                      ? formatCurrencyAmount(results.fraCombined)
                      : "$0.00"}
                  </div>
                </div>
                <div className="social-security-calultor-results">
                  <div className="social-result-heading">Taxable FRA:</div>
                  <div className="social-result-value">
                    {results.fraTaxable
                      ? formatCurrencyAmount(results.fraTaxable)
                      : "$0.00"}
                  </div>
                </div>
                <div className="social-security-calultor-results">
                  <div className="social-result-heading">Lifetime FRA PV:</div>
                  <div className="social-result-value">
                    {results.lifetimeFRA_PV
                      ? formatCurrencyAmount(results.lifetimeFRA_PV)
                      : "$0.00"}
                  </div>
                </div>
                <div className="social-security-calultor-results">
                  <div className="social-result-heading">Survivor FRA:</div>
                  <div className="social-result-value">
                    {results.fraSurvivorBenefit
                      ? formatCurrencyAmount(results.fraSurvivorBenefit)
                      : "$0.00"}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
